// Shared slide pagination + typography rules. Mirrored in backend/export_utils.py — keep both in sync.
// Slide canvas is 13.333in x 7.5in (960pt x 540pt). Body text white, verse labels 80% of body size.
export const SLIDE_W_PT = 960;
export const SLIDE_H_PT = 540;
export const DEFAULT_FONT = "Arial";
export const DEFAULT_FONT_PT = 38;
export const MIN_FONT_PT = 36;
export const MAX_FONT_PT = 46;
export const FONT_OPTIONS = ["Arial", "Verdana", "Tahoma", "Trebuchet MS"];
export const MIN_TRANSITION_MS = 500;
export const MAX_TRANSITION_MS = 2000;
export const DEFAULT_TRANSITION_MS = 700;
export const SLIDE_BREAK = "---";
export const WORDS_PER_MINUTE = 110;

const STANZA_CATEGORIES = ["Hymns/Songs", "Chants"];
const LABEL_RE = /^((pre-?)?chorus|refrain|bridge|verse|coda|tag|intro|outro|antiphon|response)\s*\d*\s*:?$/i;
const BREAK_RE = /^\s*(-{3,}|\[break\])\s*$/i;

export const isSlideBreak = (text) => BREAK_RE.test(text);

export function slideMetrics(fontPt = DEFAULT_FONT_PT) {
  const size = Math.min(MAX_FONT_PT, Math.max(MIN_FONT_PT, fontPt || DEFAULT_FONT_PT));
  return {
    bodyPt: size,
    labelPt: Math.round(size * 0.8),
    charsPerLine: Math.round(1680 / size),
    labelCharsPerLine: Math.round(2080 / size),
    maxLines: Math.floor(320 / size),
  };
}

const isLabel = (text) => LABEL_RE.test(text.trim());
const toLine = (text) => ({ text, label: isLabel(text) });
const visualLines = (line, m) =>
  Math.max(1, Math.ceil(line.text.length / (line.label ? m.labelCharsPerLine : m.charsPerLine)));
const blockHeight = (block, m) => block.reduce((n, l) => n + visualLines(l, m), 0);

function splitSentences(text) {
  const parts = text.match(/[^.!?;:]+[.!?;:]+["')\]]*\s*|[^.!?;:]+$/g) || [text];
  return parts.map((p) => p.trim()).filter(Boolean);
}

// Prefer a comma/dash boundary, else a word boundary — never mid-word.
function findCut(text, max) {
  const slice = text.slice(0, max);
  const punct = Math.max(slice.lastIndexOf(", "), slice.lastIndexOf("; "), slice.lastIndexOf(" — "), slice.lastIndexOf(" – "));
  if (punct > max * 0.45) return punct + 1;
  const space = slice.lastIndexOf(" ");
  return space > 0 ? space : max;
}

// Break a paragraph that overflows one slide into slide-sized pieces at sentence boundaries.
function splitLongText(text, m) {
  const max = m.maxLines * m.charsPerLine;
  const pieces = [];
  let current = "";
  for (const sentence of splitSentences(text)) {
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (current && candidate.length > max) {
      pieces.push(current);
      current = sentence;
    } else {
      current = candidate;
    }
    while (current.length > max) {
      const cut = findCut(current, max);
      pieces.push(current.slice(0, cut).trim());
      current = current.slice(cut).trim();
    }
  }
  if (current) pieces.push(current);
  return pieces;
}

// Avoid an orphan: a final slide holding a single line when the previous one is full.
function rebalance(chunks) {
  if (chunks.length < 2) return chunks;
  const last = chunks[chunks.length - 1];
  const prev = chunks[chunks.length - 2];
  if (last.length === 1 && prev.length >= 3 && !prev[prev.length - 1].label) {
    last.unshift(prev.pop());
  }
  return chunks;
}

function chunkBlock(block, m) {
  const chunks = [];
  let current = [];
  let height = 0;
  for (const line of block) {
    const h = visualLines(line, m);
    if (current.length && height + h > m.maxLines) {
      chunks.push(current);
      current = [];
      height = 0;
    }
    current.push(line);
    height += h;
  }
  if (current.length) chunks.push(current);
  return rebalance(chunks);
}

function splitBlocks(text, m) {
  const blocks = [];
  for (const raw of (text || "").split(/\n\s*\n/)) {
    const lines = [];
    for (const l of raw.split("\n")) {
      const trimmed = l.trim();
      if (!trimmed) continue;
      const line = toLine(trimmed);
      if (visualLines(line, m) > m.maxLines) {
        splitLongText(trimmed, m).forEach((piece) => lines.push(toLine(piece)));
      } else {
        lines.push(line);
      }
    }
    if (lines.length) blocks.push(lines);
  }
  return blocks;
}

function paginateSection(text, stanzaMode, m) {
  const blocks = splitBlocks(text, m);
  if (!blocks.length) return [];
  const pages = [];
  if (stanzaMode) {
    for (const block of blocks) chunkBlock(block, m).forEach((c) => pages.push(c));
    return pages;
  }
  let current = [];
  let height = 0;
  for (const block of blocks) {
    const h = blockHeight(block, m);
    if (h > m.maxLines) {
      if (current.length) {
        pages.push(current);
        current = [];
        height = 0;
      }
      chunkBlock(block, m).forEach((c) => pages.push(c));
      continue;
    }
    if (current.length && height + h + 1 > m.maxLines) {
      pages.push(current);
      current = [];
      height = 0;
    }
    if (current.length) {
      current.push({ text: "", label: false });
      height += 1;
    }
    current.push(...block);
    height += h;
  }
  if (current.length) pages.push(current);
  return pages;
}

function segmentPages(segment, m) {
  const stanzaMode = STANZA_CATEGORIES.includes(segment.category);
  const sections = (segment.body || "")
    .split("\n")
    .reduce(
      (acc, line) => {
        if (isSlideBreak(line)) acc.push([]);
        else acc[acc.length - 1].push(line);
        return acc;
      },
      [[]]
    )
    .map((lines) => lines.join("\n"));

  const pages = [];
  for (const section of sections) paginateSection(section, stanzaMode, m).forEach((p) => pages.push(p));
  return pages.length ? pages : [[]];
}

export function buildSlides(segments = [], fontPt = DEFAULT_FONT_PT) {
  const m = slideMetrics(fontPt);
  const slides = [];
  segments.forEach((segment, segIndex) => {
    const pages = segmentPages(segment, m);
    pages.forEach((lines, i) => {
      slides.push({
        segmentIndex: segIndex,
        category: segment.category || "",
        title: segment.title || "Untitled segment",
        durationMinutes: segment.duration_minutes || 5,
        lines,
        part: i + 1,
        partsTotal: pages.length,
      });
    });
  });
  return slides;
}

export function slideFooter(slide) {
  const label = [slide.category?.toUpperCase(), slide.title].filter(Boolean).join(" · ");
  return slide.partsTotal > 1 ? `${label} (${slide.part}/${slide.partsTotal})` : label;
}

export function spokenSeconds(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.round((words / WORDS_PER_MINUTE) * 60);
}

export function formatClock(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
